import React, { useState } from 'react';
import {
  Container, Card, CardContent, Typography, TextField, Button, Alert, Box, Divider, Link
} from '@mui/material';
import axios from 'axios';
import { useOrg } from '../context/OrgContext';

/**
 * Public page where an approved provider connects their Towbook account.
 * They paste the "external link" Towbook generates for the WKMC account, and
 * from then on every White Knight call we offer them also lands in Towbook.
 */
const DispatchIntegration = () => {
  const { name, supportEmail, supportPhone } = useOrg();
  const [form, setForm] = useState({ vendorId: '', email: '', link: '' });
  const [status, setStatus] = useState('idle'); // idle | saving | success | error
  const [message, setMessage] = useState('');

  const handleChange = (e) => setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (status === 'saving') return;
    setStatus('saving');
    setMessage('');
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/dispatch-integration/towbook`,
        form
      );
      setStatus('success');
      setMessage(data.message);
    } catch (err) {
      setStatus('error');
      setMessage(err.response?.data?.message || `Something went wrong. Please try again or contact ${supportEmail}.`);
    }
  };

  return (
    <Container maxWidth="sm">
      <Card sx={{ mt: 6, mb: 6 }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h4" gutterBottom>Connect Towbook</Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            Get your {name} calls delivered straight into Towbook, alongside
            your other motor clubs — with a second alert in case you miss the one
            in our app.
          </Typography>

          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>First, set up the link in Towbook:</Typography>
            <Box component="ol" sx={{ pl: 2, m: 0 }}>
              <li>Sign in to Towbook and open <strong>Accounts</strong>.</li>
              <li>Click <strong>Add Account</strong> and set the Account Name to <strong>{name}</strong>.</li>
              <li>Open the <strong>Users</strong> tab.</li>
              <li>Check <strong>Enable external link for this account</strong>.</li>
              <li><strong>Copy the link</strong>, then click <strong>Save</strong>.</li>
            </Box>
          </Alert>

          {status === 'success' && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}
          {status === 'error' && <Alert severity="error" sx={{ mb: 2 }}>{message}</Alert>}

          {status !== 'success' && (
            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth margin="normal" required
                label={`Your ${name} Vendor ID`}
                name="vendorId"
                value={form.vendorId}
                onChange={handleChange}
                placeholder="LA00000123"
                helperText="From your approval email"
              />
              <TextField
                fullWidth margin="normal" required
                label="Your Towbook email address"
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
              />
              <TextField
                fullWidth margin="normal" required
                label="Towbook external link"
                name="link"
                value={form.link}
                onChange={handleChange}
                placeholder="https://public.towbook.com/XXXX"
              />
              <Button
                type="submit" variant="contained" fullWidth sx={{ mt: 3 }}
                disabled={status === 'saving'}
              >
                {status === 'saving' ? 'Connecting…' : 'Connect Towbook'}
              </Button>
            </form>
          )}

          <Divider sx={{ my: 3 }} />
          <Typography variant="body2" color="text.secondary">
            <strong>Important:</strong> you still accept, update, and complete calls in the
            {name} portal or driver app. The Towbook entry is a copy for your
            records and a backup alert.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Need a hand? <Link href={`mailto:${supportEmail}`}>{supportEmail}</Link> or {supportPhone}.
          </Typography>
        </CardContent>
      </Card>
    </Container>
  );
};

export default DispatchIntegration;
